import clsx from 'clsx';
import { ReactNode, useMemo } from 'react';
import {
    ControllerFieldState,
    ControllerRenderProps,
    FieldValues,
} from 'react-hook-form';
import { InputHintError, InputLabel } from '../../_utils/components';
import { type FieldLengthRule, lengthRuleValue } from '../../_utils/rhf-ref';

export interface InputWrapperProps extends InputWrapperSharedProps {
    field?: ControllerRenderProps<FieldValues, string>;
    fieldState?: ControllerFieldState;
    children?: ReactNode;
    minLength?: FieldLengthRule;
    maxLength?: FieldLengthRule;
}

export type InputWrapperSharedProps = {
    id?: string;
    name?: string;
    label?: string | ReactNode;
    labelHidden?: boolean;
    labelTooltip?: string;
    horizontal?: boolean;
    errorMessage?: string;
    hint?: ReactNode;
    labelClassName?: string;
    wrapperClassName?: string;
    suffix?: ReactNode;
    labelSuffix?: ReactNode;
    focus?: boolean;
    required?: boolean;
    inputClassName?: string;
    errorClassName?: string;
    suppressRequiredLabel?: boolean;
    bulkMode?: boolean;
    placeholder?: string;
    'data-testid'?: string;
};

export function InputWrapper(props: InputWrapperProps): ReactNode {
    const {
        wrapperClassName,
        labelClassName,
        inputClassName,
        errorClassName,
        horizontal,
        children,
        field,
        fieldState,
        id,
        label,
        hint,
        errorMessage: errorMessageProps,
        suffix,
        labelSuffix,
        focus,
        labelHidden,
        required,
        labelTooltip,
        suppressRequiredLabel,
        bulkMode,
        minLength,
        maxLength,
    } = props;

    const errorMessage = useMemo(() => {
        if (errorMessageProps) {
            return errorMessageProps;
        }

        if (fieldState?.error?.message) {
            return undefined;
        }

        if (fieldState?.error?.type === 'required') {
            return 'This field is required';
        }

        if (fieldState?.error?.type === 'minLength') {
            const min = lengthRuleValue(minLength);
            return min
                ? `Must be at least ${min} characters`
                : 'This field is too short';
        }

        if (fieldState?.error?.type === 'maxLength') {
            const max = lengthRuleValue(maxLength);
            return max
                ? `Must be at most ${max} characters`
                : 'This field is too long';
        }
    }, [errorMessageProps, fieldState?.error, minLength, maxLength]);

    return (
        <div
            className={clsx(
                wrapperClassName,
                'input-wrapper',
                !horizontal
                    ? 'input-wrapper--vertical'
                    : 'input-wrapper--horizontal',
                !label ? 'missing-label' : null,
                !field ? 'uncontrolled' : null,
            )}
        >
            {label ? (
                <div className="input-wrapper__label">
                    <div className="input-wrapper__label-content">
                        {typeof label === 'string' ? (
                            <InputLabel
                                field={field}
                                label={label}
                                id={id}
                                labelHidden={labelHidden}
                                labelClassName={labelClassName}
                                horizontal={horizontal}
                                required={required}
                                labelTooltip={labelTooltip}
                                suppressRequiredLabel={suppressRequiredLabel}
                                bulkMode={bulkMode}
                            />
                        ) : (
                            label
                        )}
                        {labelSuffix ? (
                            <div className="input-wrapper__label-suffix">
                                {labelSuffix}
                            </div>
                        ) : null}
                    </div>
                </div>
            ) : null}
            <div
                className={clsx(
                    suffix ? 'input-wrapper__input--suffix' : null,
                    focus && suffix
                        ? 'input-wrapper__input--suffix--focus'
                        : null,
                    'input-wrapper__input',
                    inputClassName,
                )}
            >
                {children}
                {suffix}
            </div>
            <InputHintError
                className={clsx('input-wrapper__hint-error', errorClassName)}
                hint={hint}
                fieldState={fieldState}
                errorMessage={errorMessage}
            />
        </div>
    );
}
